import { useEffect, useRef, useState } from "react";
import { API } from "../lib/api";

const WATERMARK = "/branding/favicon_symbol_1024_transparent.png";

/**
 * Homepage hero right-side visual:
 * soft Nexora symbol watermark + admin-managed photo slideshow.
 * - auto-rotates every 5s when >1 active slide (fade transition)
 * - object-cover, aspect preserved, dot controls
 * - watermark-only fallback when no slides are active
 */
export default function HeroSlideshow() {
  const [slides, setSlides] = useState(null);
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/site/hero-slides`)
      .then((r) => r.json())
      .then((d) => setSlides(d.items || []))
      .catch(() => setSlides([]));
  }, []);

  useEffect(() => {
    if (!slides || slides.length <= 1) return;
    timerRef.current = setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      5000
    );
    return () => clearInterval(timerRef.current);
  }, [slides]);

  if (slides === null) {
    return <div className="hidden lg:block" aria-hidden="true" />;
  }

  const hasSlides = slides.length > 0;

  return (
    <div
      className={`relative ${hasSlides ? "" : "hidden lg:block"}`}
      data-testid="hero-slideshow"
    >
      {/* Oversized soft Nexora symbol watermark, centered behind the photo card */}
      <img
        src={WATERMARK}
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[118%] lg:w-[145%] max-w-none opacity-[0.15] object-contain z-0"
        data-testid="hero-watermark"
      />

      {hasSlides ? (
        <div
          className="relative z-10 rounded-[2rem] overflow-hidden shadow-lg shadow-slate-900/10 border border-white/70 bg-white h-56 sm:h-72 lg:h-[26rem]"
          data-testid="hero-slideshow-card"
        >
          {slides.map((s, i) => (
            <div
              key={s.id}
              className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
              style={{ opacity: i === index ? 1 : 0 }}
              aria-hidden={i !== index}
            >
              <img
                src={`${API.replace(/\/api$/, "")}${s.image_url}`}
                alt={s.alt_text || "Nexora program photo"}
                className="h-full w-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
                data-testid={`hero-slide-img-${i}`}
              />
              {s.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/70 to-transparent px-5 pb-4 pt-10">
                  <p
                    className="text-white text-xs sm:text-sm font-semibold drop-shadow"
                    data-testid={`hero-slide-caption-${i}`}
                  >
                    {s.caption}
                  </p>
                </div>
              )}
            </div>
          ))}

          {slides.length > 1 && (
            <div
              className="absolute bottom-3 right-4 flex gap-1.5 z-10"
              data-testid="hero-slideshow-dots"
            >
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Show photo ${i + 1}`}
                  data-testid={`hero-slideshow-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === index ? "w-5 bg-white" : "w-2 bg-white/50 hover:bg-white/80"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Watermark-only fallback panel — clean, never looks broken */
        <div
          className="relative rounded-[2rem] border border-slate-200/60 bg-white/50 backdrop-blur-sm h-72 lg:h-[26rem] flex items-center justify-center overflow-hidden"
          data-testid="hero-slideshow-fallback"
        >
          <img
            src={WATERMARK}
            alt=""
            aria-hidden="true"
            className="w-44 lg:w-56 opacity-[0.14] object-contain"
          />
        </div>
      )}
    </div>
  );
}
